import { motion } from "framer-motion";
import { Smile, Sparkles, HeartHandshake, ShieldCheck, ScanLine, FlaskConical } from "lucide-react";

const STATS = [
  { value: "10,000+", label: "Happy Smiles" },
  { value: "15+",     label: "Years of Practice" },
  { value: "4.9★",    label: "Google Rating" },
  { value: "12+",     label: "Services Offered" },
  { value: "Mon–Sat", label: "Open Six Days" },
  { value: "Powai · Andheri", label: "Serving Mumbai East" },
];

const FEATURES = [
  { icon: HeartHandshake, title: "Diagnosis & Treatment Plan", body: "Digital planning with a clear, unhurried consultation before anything begins." },
  { icon: ScanLine,       title: "Digital X-rays & Imaging",   body: "Low-radiation OPG & RVG with sharper results and faster diagnosis." },
  { icon: FlaskConical,   title: "Modern Sterilisation",       body: "Autoclaved instruments and single-use consumables — every appointment, no exceptions." },
  { icon: Sparkles,       title: "Laser Gum Therapy",          body: "Diode-laser treatments with minimal discomfort and faster healing." },
  { icon: Smile,          title: "Digital Smile Design",       body: "Preview your new smile in a computer mock-up before treatment starts." },
  { icon: ShieldCheck,    title: "Gentle-first Philosophy",    body: "Small caseload, calming ambience and a team trained in reducing dental anxiety." },
];

export default function WhyUs() {
  return (
    <section id="why" data-testid="why-us">
      {/* Stats band */}
      <div className="relative bg-[hsl(var(--primary-dark))] text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20 -z-0">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-[hsl(var(--primary))] blur-3xl" />
          <div className="absolute bottom-0 right-10 w-96 h-96 rounded-full bg-[hsl(var(--accent))] blur-3xl" />
        </div>
        <div className="max-w-[1440px] mx-auto px-5 md:px-12 relative">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <div className="eyebrow text-white/80 mb-3" style={{ color: "hsl(var(--primary-light))" }}>Clinic highlights</div>
            <h2 className="font-display text-2xl md:text-5xl leading-tight">
              Why patients choose <br className="hidden md:block"/> <span className="text-[hsl(var(--accent))]">Calm Dentistry</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.6 }}
                className="stat-card p-4 md:p-6 text-center"
                data-testid={`stat-${i}`}
              >
                <div className="font-display text-xl md:text-3xl leading-tight">{s.value}</div>
                <div className="text-[11px] md:text-sm mt-1 md:mt-2 opacity-90 leading-tight">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Unique features */}
      <div className="py-20 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <div className="eyebrow mb-4 justify-center">Why us</div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-[-0.02em]">
              Unique dental features <br /><span className="text-[hsl(var(--primary))]">at Calm Dentistry</span>
            </h2>
            <p className="mt-5 md:mt-6 text-[15px] md:text-base text-[hsl(var(--ink-soft))]">
              Advanced diagnosis, digital imaging, and precise technology for faster, safer, calmer dental care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.55 }}
                className="card-soft p-5 md:p-7"
                data-testid={`feature-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--primary-light))] text-[hsl(var(--primary-dark))] flex items-center justify-center mb-5">
                  <f.icon size={24} strokeWidth={1.7} />
                </div>
                <h3 className="font-display text-lg mb-2">{f.title}</h3>
                <p className="text-[14px] text-[hsl(var(--ink-soft))] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
